import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { CardSaveBadge, type CardSavePhase } from '../tenant/CardSaveBadge';
import ClosingDocumentSheet from './ClosingDocumentSheet';
import ClosingDocumentUpload from './ClosingDocumentUpload';
import ClosingETransferStep from './ClosingETransferStep';
import PadAgreementInstructions, { PadNumberedSteps } from './PadAgreementInstructions';
import {
  eTransferIsComplete,
  padIsComplete,
  voidChequeIsComplete,
  type ClosingMember,
  type ClosingPersonalState,
} from './closing-model';
import { CLOSING_CARD, CLOSING_DOCUMENT_PANEL, closingStatusClass } from './closing-styles';

interface ClosingPersonStepsProps {
  person: ClosingMember;
  state: ClosingPersonalState;
  padFile: File | null;
  voidChequeFile: File | null;
  eTransferFile: File | null;
  onPadFile: (file: File) => void;
  onClearPad: () => void;
  onReplacePad?: () => void;
  onVoidChequeFile: (file: File) => void;
  onClearVoidCheque: () => void;
  propertyLabel: string;
  onETransferFile: (file: File) => void;
  onClearETransfer: () => void;
  onDeleteETransfer?: () => void;
  padSavePhase?: CardSavePhase;
  voidChequeSavePhase?: CardSavePhase;
  eTransferSavePhase?: CardSavePhase;
  getSavedDocumentUrl?: (documentId: string) => Promise<string>;
  padDocumentId?: string | null;
  voidChequeDocumentId?: string | null;
  eTransferDocumentId?: string | null;
  onDeletePad?: () => void;
  onDeleteVoidCheque?: () => void;
  padPlaid?: ReactNode;
  onSendBlankPad?: () => Promise<void> | void;
}

function CompleteMark({ done, label }: { done: boolean; label: string }) {
  if (!done) return null;
  return (
    <span className="absolute right-4 top-4 z-10 grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700 sm:right-5 sm:top-5" aria-label={`${label} complete`}>
      <Check size={16} strokeWidth={2.5} aria-hidden="true" />
    </span>
  );
}

function ReadOnlyDocument({ title, done, ownerName }: { title: string; done: boolean; ownerName: string }) {
  const status = (
    <div
      className={`rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm dark:border-white/10 dark:bg-white/5 ${closingStatusClass(done)}`}
      role="status"
    >
      {done ? 'Complete' : 'Incomplete'}
    </div>
  );
  return (
    <section className={`${CLOSING_CARD} flex h-full flex-col`}>
      <CompleteMark done={done} label={title} />
      <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
      {done ? (
        <div className="mt-3">{status}</div>
      ) : (
        <span className="group relative mt-3 block">
          {status}
          <span
            role="tooltip"
            className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 w-max max-w-[14rem] -translate-x-1/2 rounded-lg bg-gray-900 px-2.5 py-1.5 text-center text-xs font-normal text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 dark:bg-gray-100 dark:text-gray-900"
          >
            {ownerName} needs to fill this out.
          </span>
        </span>
      )}
    </section>
  );
}

const PAD_EMPTY_PREVIEW = 'Your pre-authorized debit agreement will appear here once signing is complete.';

function PadAwaitingSignature() {
  return (
    <div className={CLOSING_DOCUMENT_PANEL}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0 flex-1">
          <PadNumberedSteps completedCount={2} />
        </div>
        <div className="flex w-full max-w-[16rem] shrink-0 justify-center sm:w-[16rem]">
          <ClosingDocumentSheet>{PAD_EMPTY_PREVIEW}</ClosingDocumentSheet>
        </div>
      </div>
    </div>
  );
}

export default function ClosingPersonSteps({
  person,
  state,
  padFile,
  voidChequeFile,
  eTransferFile,
  onPadFile,
  onClearPad,
  onReplacePad,
  onVoidChequeFile,
  onClearVoidCheque,
  propertyLabel,
  onETransferFile,
  onClearETransfer,
  onDeleteETransfer,
  padSavePhase = 'idle',
  voidChequeSavePhase = 'idle',
  eTransferSavePhase = 'idle',
  getSavedDocumentUrl,
  padDocumentId = null,
  voidChequeDocumentId = null,
  eTransferDocumentId = null,
  onDeletePad,
  onDeleteVoidCheque,
  padPlaid,
  onSendBlankPad,
}: ClosingPersonStepsProps) {
  const padDone = padIsComplete(state);
  const padAwaitingSignature = state.padSigned && !state.padFileName;
  const voidDone = voidChequeIsComplete(state);
  const transferDone = eTransferIsComplete(state);

  if (!person.isViewer) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          You can see {person.displayName}&apos;s closing steps. Each step shows only whether it is complete.
        </p>
        <div className="grid grid-cols-2 items-start gap-4">
          <ReadOnlyDocument title="Pre-Authorized Debit (PAD agreement)" done={padDone} ownerName={person.displayName} />
          <ReadOnlyDocument title="Void Cheque" done={voidDone} ownerName={person.displayName} />
          <ReadOnlyDocument title="Last Month's Rent Deposit" done={transferDone} ownerName={person.displayName} />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 items-start gap-4">
      <section className={`${CLOSING_CARD} flex min-w-0 flex-col`}>
        <CardSaveBadge phase={padSavePhase} complete={padDone && !padAwaitingSignature} />
        <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Pre-Authorized Debit (PAD agreement)</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Select the bank account you would like to make your rent payments from.
        </p>
        {state.padSigned && !state.padFileName ? (
          <PadAwaitingSignature />
        ) : (
          <ClosingDocumentUpload
            note=""
            instructionsExtra={
              <PadAgreementInstructions
                completedCount={state.padFileName ? 3 : 0}
                plaid={state.padFileName ? undefined : padPlaid}
                onSendBlank={state.padFileName ? undefined : onSendBlankPad}
                blankSending={padSavePhase === 'saving'}
              />
            }
            fileName={state.padFileName}
            savedDocumentId={padDocumentId}
            getSavedDocumentUrl={getSavedDocumentUrl}
            file={padFile}
            onFile={onPadFile}
            onClear={onClearPad}
            onDeleteSaved={onDeletePad}
            onReplaceSaved={onReplacePad}
            disabled={padSavePhase === 'saving'}
            reviewStatus={{
              review: state.padReview,
              hasSubmission: Boolean(state.padFileName),
            }}
            emptyPreview={PAD_EMPTY_PREVIEW}
            previewSheet
            sheetPanel
          />
        )}
      </section>

      <section className={`${CLOSING_CARD} flex min-w-0 flex-col`}>
        <CardSaveBadge phase={voidChequeSavePhase} complete={voidDone} />
        <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Void Cheque</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Upload a void cheque so we can verify your payment details.
        </p>
        <ClosingDocumentUpload
          note=""
          emptyPreview="Your void cheque will appear here."
          previewSheet
          fileName={state.voidChequeFileName}
          savedDocumentId={voidChequeDocumentId}
          getSavedDocumentUrl={getSavedDocumentUrl}
          file={voidChequeFile}
          onFile={onVoidChequeFile}
          onClear={onClearVoidCheque}
          onDeleteSaved={onDeleteVoidCheque}
          disabled={voidChequeSavePhase === 'saving'}
          reviewStatus={{
            review: state.voidChequeReview,
            hasSubmission: Boolean(state.voidChequeFileName),
          }}
        />
      </section>

      <ClosingETransferStep
        state={state}
        personName={person.displayName}
        propertyLabel={propertyLabel}
        disabled={false}
        savePhase={eTransferSavePhase}
        complete={transferDone}
        file={eTransferFile}
        savedDocumentId={eTransferDocumentId}
        getSavedDocumentUrl={getSavedDocumentUrl}
        onFile={onETransferFile}
        onClear={onClearETransfer}
        onDeleteSaved={onDeleteETransfer}
      />
    </div>
  );
}
